/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { FormikProps } from 'formik';
import { TextV2, FormControl, InputV2, SpacingStack, SelectV1, OptionType, Flex, IconV2 } from '@withjoy/joykit';
import { ChevronDown } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { Contacts, ContactInputFields } from '@apps/contactCollector/guest/ContactCollectorGuest.controller';
import { Contacts as IframeContacts, ContactInputFields as IframeContactInputFields } from '@apps/contactCollector/iframe/ContactCollectorIframe.controller';
import { countryList } from '../../Form.controller';

interface MailingAddressProps
  extends Readonly<{
    formik: FormikProps<Contacts> | FormikProps<IframeContacts>;
    index: number;
    contact: ContactInputFields | IframeContactInputFields;
    handleOnCountryDropdownChange: (option: OptionType | null, index: number) => void;
    errors: {
      address1Error: (index: number) => any;
      postalCodeError: (index: number) => any;
      cityError: (index: number) => any;
      regionError: (index: number) => any;
      countryError: (index: number) => any;
    };
    isIframeVersion?: boolean;
  }> {}

export const MailingAddress: React.FC<MailingAddressProps> = ({
  formik,
  index,
  contact,
  handleOnCountryDropdownChange,
  errors: { address1Error, postalCodeError, cityError, regionError, countryError },
  isIframeVersion = false
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { t } = useTranslation('contactCollector');
  const tAdmin = t('admin');

  return (
    <>
      <Flex
        gap={2}
        alignItems="center"
        marginTop="2rem !important"
        onClick={() => setIsOpen(prevState => !prevState)}
        cursor="pointer"
        marginBottom="12px"
        justifyContent="space-between"
      >
        <TextV2 typographyVariant="hed4" width="100%">
          {tAdmin.mailingAddress()}
        </TextV2>
        {isIframeVersion && (
          <IconV2 size="md" style={{ transform: isOpen ? 'rotate(-180deg)' : 'rotate(0deg)', transition: 'transform 500ms cubic-bezier(0.23,1,0.32,1) 0s' }}>
            <ChevronDown />
          </IconV2>
        )}
      </Flex>
      {(isOpen || !isIframeVersion) && (
        <>
          <FormControl label={tAdmin.address1()} isInvalid={!!address1Error(index)} error={address1Error(index)} isRequired>
            <InputV2 {...formik.getFieldProps(`contacts[${index}].address1`)} autoComplete="address-line1" />
          </FormControl>
          <FormControl label={tAdmin.address2()}>
            <InputV2 {...formik.getFieldProps(`contacts[${index}].address2`)} autoComplete="address-line2" />
          </FormControl>
          <FormControl label={tAdmin.city()} isInvalid={!!cityError(index)} error={cityError(index)} isRequired>
            <InputV2 {...formik.getFieldProps(`contacts[${index}].city`)} autoComplete="address-level2" />
          </FormControl>
          <FormControl label={tAdmin.region()} isInvalid={!!regionError(index)} error={regionError(index)} isRequired>
            <InputV2 {...formik.getFieldProps(`contacts[${index}].region`)} autoComplete="address-level1" />
          </FormControl>
          <FormControl label={tAdmin.postalCode()} isInvalid={!!postalCodeError(index)} error={postalCodeError(index)} isRequired>
            <InputV2 {...formik.getFieldProps(`contacts[${index}].postalCode`)} autoComplete="postal-code" />
          </FormControl>
          <FormControl label={tAdmin.country()} isInvalid={!!countryError(index)} error={countryError(index)}>
            <SpacingStack spacing={3}>
              <SelectV1
                options={countryList}
                value={{ label: contact.country, value: contact.countryCode } as OptionType}
                searchable={false}
                onChange={value => handleOnCountryDropdownChange(value, index)}
              />
            </SpacingStack>
          </FormControl>
        </>
      )}
    </>
  );
};
